// THE v2 BASE URL: ITS STATES AND ITS ONE READER (W4.1; moved out of `http.ts` by B8, LAUNCH-COPY-B8).
//
// `VITE_V2_BASE_URL` is read by two programs now, and by this one function in both: the client (`main.tsx`, through
// `http.ts`'s re-export) and the build (`web/deployTargets.ts`, loaded by `vite.config.ts` in Node, which refuses a
// production build whose base URL is not production's and writes the Content-Security-Policy's `connect-src` from it). So
// this file imports nothing: a build config cannot load the generated client `http.ts` imports.
//
// `VITE_V2_BASE_URL=emulator` points the client at the LOCAL FUNCTIONS EMULATOR, on loopback, under the `demo-` project id
// (see `http.ts`'s header for why that one value is not a guess). Every other value must be an absolute `http(s)` URL.

/** The build variable that decides everything below. Unset in every committed file. */
export const BASE_URL_VAR = "VITE_V2_BASE_URL";

/** The one value of `VITE_V2_BASE_URL` that is not a URL: it selects the local emulator's shape. */
export const EMULATOR_TOKEN = "emulator";

/** The emulator's own coordinates, as the functions emulator prints them on boot and as W3's harness calls them. */
export const EMULATOR_ORIGIN = "http://127.0.0.1:5001";
export const EMULATOR_PROJECT = "demo-proppeal-v2";
export const EMULATOR_REGION = "us-central1";

/**
 * WHAT A BUILD'S BASE URL IS. Four states, and "absent" and "malformed" are states, not errors to paper over.
 *
 * There is no fifth state and there is deliberately no default: a fallback origin is how a client ends up talking to
 * production because someone forgot a variable, and a client that falls back to its OWN page origin (which is what a
 * relative URL silently does) is the same defect wearing a different hat.
 */
export type V2BaseUrl =
  /** No variable. Every build this repository produces. Nothing can be called. */
  | { readonly kind: "absent" }
  /** One absolute origin, possibly with a path prefix. The endpoint's name is appended. */
  | { readonly kind: "origin"; readonly origin: string }
  /** The local functions emulator. */
  | { readonly kind: "emulator" }
  /** A variable was set and is not usable. LOUD, and never treated as absent-with-a-shrug: someone meant to point
   *  this build somewhere, and it is pointing nowhere. */
  | { readonly kind: "malformed"; readonly value: string; readonly problem: string };

/** The reachable states — the only two from which a URL can be built. */
export type V2ReachableBaseUrl = Extract<V2BaseUrl, { kind: "origin" } | { kind: "emulator" }>;

/** True when a base URL can produce a URL at all. */
export function isReachable(base: V2BaseUrl): base is V2ReachableBaseUrl {
  return base.kind === "origin" || base.kind === "emulator";
}

/**
 * Reads `VITE_V2_BASE_URL` into one of the four states. THE ONLY PLACE THAT VARIABLE IS INTERPRETED.
 *
 * A value must be an absolute `http:` or `https:` URL with no query and no fragment, or the literal `emulator`.
 * Everything else is `malformed` — including a bare hostname and a leading-slash path, both of which a browser would
 * happily resolve against whatever origin the page was served from.
 */
export function readBaseUrl(raw: string | undefined): V2BaseUrl {
  if (raw === undefined || raw.trim() === "") return { kind: "absent" };
  const value = raw.trim();
  if (value === EMULATOR_TOKEN) return { kind: "emulator" };

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return { kind: "malformed", value, problem: `${BASE_URL_VAR} is not an absolute URL (it must start with https://)` };
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return { kind: "malformed", value, problem: `${BASE_URL_VAR} must be http or https, not ${parsed.protocol}` };
  }
  if (parsed.search !== "" || parsed.hash !== "") {
    return { kind: "malformed", value, problem: `${BASE_URL_VAR} must carry no query string and no fragment` };
  }
  return { kind: "origin", origin: value.replace(/\/+$/, "") };
}
